import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { CrazyCallConfigService } from './crazycall-config.service';
import { AppConfigService } from './app-config.service';
import { Configuration, ConfigurationResponse } from '../domain/configuration.model';
import { Observable } from 'rxjs/Observable';
import { mapResponse } from '../domain/configurationMapper';
import { CreateProjectResponse } from '../models/Project';
import { flatMap } from 'rxjs/operators';
import { ExistsResponse } from '../domain/ExistsResponse';

@Injectable()
export class LeadGenerationConfigurationService {
  constructor(
    private http: HttpClient,
    private crazyCallConfigService: CrazyCallConfigService,
    private appConfigService: AppConfigService
  ) {}

  leadGenerationExists(): Observable<boolean> {
    const appId = this.crazyCallConfigService.appId;
    return this.http
      .get(`${this.appConfigService.WEB_CALL_BACKEND_CONFIGURATOR_URL}configuration/exists`, {
        params: { appId },
      })
      .map(({ exists }: ExistsResponse) => exists);
  }

  createProject(config: Configuration): Observable<CreateProjectResponse> {
    return this.leadGenerationExists().pipe(
      flatMap((exists: boolean) => (exists ? this.createNewProject(config) : this.saveInitialConfiguration(config)))
    );
  }

  addOrigin(origin: string): Observable<any> {
    return this.http.post('/lead-generation/settings/add-origin', { origin });
  }

  updateConfiguration(config: Configuration): Observable<Configuration> {
    return this.http
      .put(`${this.baseUrl}/projects/${config.projectId}`, this.mapConfigTimeToSeconds(config), {})
      .map((data: ConfigurationResponse) => mapResponse(data, config.projectId));
  }

  saveInitialConfiguration(config: Configuration): Observable<CreateProjectResponse> {
    return this.postWithConfiguration('configuration', config);
  }

  saveDefaultConfiguration(config: Configuration): Observable<HttpResponse<ConfigurationResponse>> {
    return this.http.post<ConfigurationResponse>(
      `${this.appConfigService.WEB_CALL_BACKEND_CONFIGURATOR_URL}configuration/default`,
      this.mapConfigTimeToSeconds(config),
      { observe: 'response' }
    );
  }

  checkInstallation(url: string): Observable<{ installed: boolean }> {
    return this.http.get<{ installed: boolean }>(`${this.baseUrl}/check-code`, {
      params: { url },
    });
  }

  private createNewProject(config: Configuration): Observable<CreateProjectResponse> {
    return this.postWithConfiguration(`${this.crazyCallConfigService.appId}/projects`, config);
  }

  private get baseUrl() {
    return this.appConfigService.WEB_CALL_BACKEND_CONFIGURATOR_URL + this.crazyCallConfigService.appId;
  }

  private postWithConfiguration(
    url: string,
    config: Configuration,
    mapConfig: (Configuration) => Configuration = this.mapConfigTimeToSeconds
  ): Observable<CreateProjectResponse> {
    return this.http.post<CreateProjectResponse>(
      `${this.appConfigService.WEB_CALL_BACKEND_CONFIGURATOR_URL}${url}`,
      mapConfig(config)
    );
  }

  // noinspection JSMethodCanBeStatic
  private mapConfigTimeToSeconds(c: Configuration): Configuration {
    return {
      ...c,
      timeBetweenShownPopups: c.timeBetweenShownPopups * 60,
      timeBetweenSubmittedPopups: c.timeBetweenSubmittedPopups * 60,
    };
  }
}
